import { ReferenceBusinessId } from "@sp-crm/core";
import { useMutation } from "@tanstack/react-query";
import { trpc } from "components/rpc/trpc";
import { handleEvent } from "util/user-events";
import { AiSummaryState } from "./ai-summary-state";
import { useEntityAiSummary } from "./use-entity-ai-summary";
import { useReferenceBusinessAiSummaryAccessQuery } from "./use-reference-business-ai-summary-access";

export const useReferenceBusinessAiSummary = (
    referenceBusinessId: ReferenceBusinessId | null,
): AiSummaryState => {
    const trpcClient = trpc.useTRPC();

    const accessQuery = useReferenceBusinessAiSummaryAccessQuery(referenceBusinessId);
    const generateMutation = useMutation({
        ...trpcClient.referenceBusinessAiSummary.generate.mutationOptions(),
    });
    const submitFeedbackMutation = useMutation({
        ...trpcClient.referenceBusinessAiSummary.submitFeedback.mutationOptions(),
    });
    const updateTextMutation = useMutation({
        ...trpcClient.referenceBusinessAiSummary.updateText.mutationOptions(),
    });
    const removeSummaryMutation = useMutation({
        ...trpcClient.referenceBusinessAiSummary.removeSummary.mutationOptions(),
    });

    return useEntityAiSummary({
        summary: accessQuery.data?.summary,
        isQueryLoading: accessQuery.isLoading,
        queryError: accessQuery.error?.message,
        refetchSummary: () => accessQuery.refetch(),

        triggerGenerate: () => {
            if (!referenceBusinessId) {
                return Promise.reject(
                    new Error(
                        "Cannot generate a reference business summary without a reference business id",
                    ),
                );
            }
            return generateMutation.mutateAsync({ entityId: referenceBusinessId });
        },
        isGenerating: generateMutation.isPending,
        generateError: generateMutation.error?.message,

        triggerSubmitFeedback: input => submitFeedbackMutation.mutate(input),
        isSubmittingFeedback: submitFeedbackMutation.isPending,
        submitFeedbackError: submitFeedbackMutation.error?.message,

        triggerUpdateText: input => updateTextMutation.mutateAsync(input),
        updateTextError: updateTextMutation.error?.message,

        triggerRemoveSummary: input => removeSummaryMutation.mutateAsync(input),
        isRemoving: removeSummaryMutation.isPending,
        removeSummaryError: removeSummaryMutation.error?.message,

        trackGenerate: () =>
            handleEvent("ai-summary-generate", {
                referenceBusinessId,
                isRegenerate: false,
            }),
        trackRegenerate: () =>
            handleEvent("ai-summary-generate", {
                referenceBusinessId,
                isRegenerate: true,
            }),
    });
};
